import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Music2, Save } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Dedicated admin page for the Spotify playlist embed shown on /spotify.
 * Extracted from the old combined Admin.jsx so the main landing page
 * stays focused on content tasks (posts, pages, theme).
 */
export default function AdminSpotify() {
  const [url, setUrl] = useState("");
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/settings/spotify");
      setCurrent(r.data);
      setUrl(r.data?.url || "");
    } catch {
      setCurrent(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    setSaved(false);
    try {
      const r = await api.put("/settings/spotify", { url: url.trim() });
      setCurrent(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--brand-paper)] text-[var(--brand-ink)]">
      <Header />
      <main className="mx-auto max-w-3xl px-5 pb-16 pt-8 sm:px-8" data-testid="admin-spotify-page">
        <Link
          to="/admin"
          data-testid="admin-spotify-back"
          className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={11} strokeWidth={2.5} /> Back to admin
        </Link>
        <div className="mt-3 flex items-center gap-2 text-brand-accent">
          <Music2 size={14} strokeWidth={2.5} />
          <span className="label-kicker">Spotify</span>
        </div>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight sm:text-4xl">
          What's on the Spotify page?
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
          Paste a Spotify playlist share URL (from the app → Share → Copy link).
          Replaces whatever&apos;s embedded on the public /spotify page right now.
        </p>

        {error ? (
          <p
            className="mt-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-spotify-error"
          >
            {error}
          </p>
        ) : null}

        <form
          onSubmit={submit}
          data-testid="admin-spotify-card"
          className="mt-6 rounded-md border border-black/10 bg-white p-6"
        >
          <label className="label-kicker text-[var(--brand-body)]">Playlist URL</label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/playlist/..."
              data-testid="admin-spotify-url"
              className="flex-1 rounded-md border border-black/15 bg-white px-4 py-2.5 font-mono text-sm outline-none focus:border-[#0A0F17]"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-spotify-save"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-white disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "Saving…" : saved ? "Saved ✓" : "Save playlist"}
            </button>
          </div>
          {current?.playlist_id ? (
            <p className="mt-3 label-kicker text-[var(--brand-body)]">
              Currently live · playlist ID {current.playlist_id}
            </p>
          ) : null}
        </form>
      </main>
      <Footer />
    </div>
  );
}
